import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { FC, ReactNode, useState } from 'react';
import { FormattedMessage, FormattedNumber, defineMessages } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { InGameCurrency, Rarity, Sport } from '__generated__/globalTypes';
import { Button } from 'atoms/buttons/Button';
import { FontAwesomeIcon } from 'atoms/icons';
import { Boost } from 'atoms/icons/Boost';
import { Cards } from 'atoms/icons/Cards';
import { Energy } from 'atoms/icons/Energy';
import { GemIcon } from 'atoms/icons/GemIcon';
import { Shards } from 'atoms/icons/Shards';
import { Horizontal, Vertical } from 'atoms/layout/flex';
import { Tooltip } from 'atoms/tooltip/Tooltip';
import { BrandL, BrandS, LabelS } from 'atoms/typography';
import {
  ANY_SPORT_MY_CARDS_ESSENCE,
  ANY_SPORT_MY_CARDS_ESSENCE_TYPE,
  ANY_SPORT_MY_CARDS_REVEALABLE_CARDS_TYPE,
  FOOTBALL_MARKET_COMMON_PACKS,
} from 'constants/__generated__/routes';
import { ANY_SPORT_COMMON_MARKET_ADD_CARD } from 'constants/routes';
import { useCurrentUserContext } from 'contexts/currentUser';
import { useSportContext } from 'contexts/sport';
import { useGetMyCardsBoostLink } from 'hooks/my-cards/useGetMyCardsBoostLink';
import { formatScarcity } from 'lib/cards';
import { glossary } from 'lib/glossary';
import { generateSportPath } from 'lib/routing/generateSportPath';
import { inGameCurrencyToRarity } from 'lib/scarcity';
import { laptopAndAbove } from 'style/mediaQuery';

export enum InGameCurrencyAgreggated {
  ENERGY = 'ENERGY',
  XP = 'XP',
  SHARDS = 'SHARDS',
  BLUEPRINTS = 'BLUEPRINT',
  GEMS = 'GEMS',
}

export type CurrencyData = {
  id: InGameCurrencyAgreggated;
  icon: ReactNode;
  amount?: number;
  details?: FC;
  visibleOnSports: Sport[];
  expanded?: boolean;
  onClick?: () => void;
};

type DetailsProps = { onClick?: () => void };

const SelectedCurrencyWrapper = styled(Vertical).attrs({ gap: 2 })`
  position: relative;
  padding: var(--double-unit) var(--unit) var(--unit) var(--unit);
  border: 1px solid var(--c-nd-150);
  border-radius: var(--intermediate-unit);
`;
const RaritiesWrapper = styled(Horizontal)`
  align-items: stretch;
  @media ${laptopAndAbove} {
    flex-direction: column;
    gap: 0;
  }
`;
const RarityWrapper = styled(Vertical)`
  justify-content: space-between;
  align-items: center;
  flex: 1;
  text-align: center;
  border-radius: var(--unit);
  padding: var(--unit);
  background-color: var(--c-nd-50);

  @media ${laptopAndAbove} {
    flex-direction: row;
    width: 100%;
    padding: var(--half-unit) var(--unit);

    background-color: transparent;
    &:hover {
      background-color: var(--c-nd-50);
    }
  }
`;

const messages = defineMessages({
  capAlmostReached: {
    id: 'useUserCurrencies.capAlmostReached',
    defaultMessage: 'Cap almost reached',
  },
});

export const useUserCurrencies = (
  expandedCurrencies?: InGameCurrencyAgreggated[]
): CurrencyData[] => {
  const navigate = useNavigate();
  const { sport } = useSportContext();
  const getMyCardsBoostLink = useGetMyCardsBoostLink();
  const { currentUser } = useCurrentUserContext();
  const [seeAllEnergy, setSeeAllEnergy] = useState(false);

  const { energyBalance, xpBalances, shardsChests, anyUserSportProfile } =
    currentUser || {};

  const { blueprintCardsCount = 0 } = anyUserSportProfile || {};

  const filteredXpBalances =
    sport !== Sport.FOOTBALL
      ? xpBalances
      : xpBalances?.filter(
          balance => balance.currency !== InGameCurrency.COMMON_XP
        );
  const totalEnergyBalance = energyBalance?.reduce(
    (sum, { currency, amount }) => {
      const rarity = inGameCurrencyToRarity[currency];
      if (!rarity) return sum;
      // do not show energy balance if user has no card of this rarity
      if (!seeAllEnergy && !currentUser?.cardCounts?.[rarity]) {
        return sum;
      }
      return sum + amount;
    },
    0
  );
  const totalShards = [
    ...(shardsChests || []),
    { cardShardsCount: currentUser?.commonCardPoints || 0 },
  ]?.reduce((acc, chest) => acc + chest.cardShardsCount, 0);
  const firstNonZeroBalance = (filteredXpBalances || []).find(
    ({ amount }) => amount > 0
  );
  const firstNonZeroBalanceRarity = firstNonZeroBalance?.currency
    ? inGameCurrencyToRarity[firstNonZeroBalance.currency]
    : undefined;
  const totalXPBalance = filteredXpBalances?.reduce(
    (sum, { amount }) => sum + amount,
    0
  );
  const gemsCount = currentUser?.gemsBalances[0]?.amount || 0;

  return [
    {
      id: InGameCurrencyAgreggated.SHARDS,
      icon: <Shards size={2} color="var(--c-green-400)" />,
      amount: totalShards,
      details: ({ onClick }: DetailsProps) => (
        <SelectedCurrencyWrapper>
          <Vertical gap={0.5} center>
            <Shards size={2} color="var(--c-green-400)" />
            <BrandS color="var(--c-green-400)">
              <FormattedMessage
                id="useUserCurrencies.essence"
                defaultMessage="Essence"
              />
            </BrandS>
            <BrandL>
              <FormattedNumber value={totalShards || 0} />
            </BrandL>
          </Vertical>
          <RaritiesWrapper>
            {sport === Sport.FOOTBALL && !!currentUser && (
              <RarityWrapper
                as={Link}
                to={FOOTBALL_MARKET_COMMON_PACKS}
                onClick={onClick}
              >
                <LabelS>{formatScarcity(Rarity.common)}</LabelS>
                <BrandS>
                  <FormattedNumber value={currentUser.commonCardPoints} />
                </BrandS>
              </RarityWrapper>
            )}
            {sport !== Sport.FOOTBALL && (
              <RarityWrapper
                as={Link}
                to={generateSportPath(ANY_SPORT_COMMON_MARKET_ADD_CARD, {
                  sport,
                })}
                onClick={onClick}
              >
                <LabelS>{formatScarcity(Rarity.common)}</LabelS>
                <BrandS>
                  <FormattedNumber value={currentUser?.commonCardPoints || 0} />
                </BrandS>
              </RarityWrapper>
            )}
            {shardsChests?.map(({ cardShardsCount, rarity }) => {
              return (
                <RarityWrapper
                  key={rarity}
                  as={Link}
                  to={generateSportPath(ANY_SPORT_MY_CARDS_ESSENCE_TYPE, {
                    params: { type: rarity },
                    sport,
                  })}
                  onClick={onClick}
                >
                  <LabelS>{formatScarcity(rarity)}</LabelS>
                  <BrandS>
                    <FormattedNumber value={cardShardsCount} />
                  </BrandS>
                </RarityWrapper>
              );
            })}
          </RaritiesWrapper>
        </SelectedCurrencyWrapper>
      ),
      visibleOnSports: [Sport.FOOTBALL, Sport.NBA, Sport.BASEBALL],
      expanded: expandedCurrencies?.includes(InGameCurrencyAgreggated.SHARDS),
      onClick: () =>
        navigate(generateSportPath(ANY_SPORT_MY_CARDS_ESSENCE, { sport })),
    },
    {
      id: InGameCurrencyAgreggated.XP,
      icon: <Boost size={2} color="var(--c-rivals-friendlies)" />,
      amount: totalXPBalance,
      details: ({ onClick }: DetailsProps) => (
        <SelectedCurrencyWrapper>
          <Vertical gap={0.5} center>
            <Boost size={2} color="var(--c-rivals-friendlies)" />
            <BrandS color="var(--c-rivals-friendlies)">
              <FormattedMessage
                id="XpBalances.xpReserve"
                defaultMessage="XP Reserve"
              />
            </BrandS>
            <BrandL>
              <FormattedNumber value={totalXPBalance || 0} />
            </BrandL>
          </Vertical>
          <RaritiesWrapper>
            {filteredXpBalances?.map(
              ({ currency, amount, capAlmostReached }) => {
                const rarity = inGameCurrencyToRarity[currency];
                if (!rarity) return null;

                return (
                  <RarityWrapper
                    key={currency}
                    as={Link}
                    to={getMyCardsBoostLink({
                      rarity,
                      sport,
                    })}
                    onClick={onClick}
                  >
                    <LabelS>{formatScarcity(rarity)}</LabelS>
                    <Horizontal>
                      <BrandS>
                        <FormattedNumber value={amount} />
                      </BrandS>
                      {!!capAlmostReached && (
                        <Tooltip
                          title={
                            <FormattedMessage {...messages.capAlmostReached} />
                          }
                        >
                          <FontAwesomeIcon
                            size="xs"
                            color="var(--c-yellow-400)"
                            icon={faTriangleExclamation}
                          />
                        </Tooltip>
                      )}
                    </Horizontal>
                  </RarityWrapper>
                );
              }
            )}
          </RaritiesWrapper>
        </SelectedCurrencyWrapper>
      ),
      visibleOnSports: [Sport.FOOTBALL, Sport.NBA, Sport.BASEBALL],
      expanded: expandedCurrencies?.includes(InGameCurrencyAgreggated.XP),
      onClick: () => {
        if (firstNonZeroBalanceRarity) {
          navigate(
            getMyCardsBoostLink({
              rarity: firstNonZeroBalanceRarity,
              sport,
            })
          );
        } else {
          navigate(FOOTBALL_MARKET_COMMON_PACKS);
        }
      },
    },
    {
      id: InGameCurrencyAgreggated.ENERGY,
      icon: <Energy size={2} color="var(--c-yellow-300)" />,
      amount: totalEnergyBalance,
      details: () => (
        <SelectedCurrencyWrapper>
          <Vertical gap={0.5} center>
            <Energy size={2} color="var(--c-yellow-300)" />
            <BrandS color="var(--c-yellow-300)">
              <FormattedMessage
                id="useUserCurrencies.energy"
                defaultMessage="Energy"
              />
            </BrandS>
            <BrandL>
              <FormattedNumber value={totalEnergyBalance || 0} />
            </BrandL>
          </Vertical>
          <RaritiesWrapper gap={0}>
            {energyBalance?.map(({ currency, amount, capAlmostReached }) => {
              const rarity = inGameCurrencyToRarity[currency];
              if (!rarity) return null;
              // do not show energy balance if user has no card of this rarity
              if (!seeAllEnergy && !currentUser?.cardCounts?.[rarity]) {
                return null;
              }

              return (
                <RarityWrapper key={currency}>
                  <LabelS>{formatScarcity(rarity)}</LabelS>
                  <Horizontal>
                    <BrandS>
                      <FormattedNumber value={amount} />
                    </BrandS>
                    {!!capAlmostReached && (
                      <Tooltip
                        title={
                          <FormattedMessage {...messages.capAlmostReached} />
                        }
                      >
                        <FontAwesomeIcon
                          size="xs"
                          color="var(--c-yellow-400)"
                          icon={faTriangleExclamation}
                        />
                      </Tooltip>
                    )}
                  </Horizontal>
                </RarityWrapper>
              );
            })}
          </RaritiesWrapper>
          <Button
            size="compact"
            fullWidth
            color="quaternary"
            disableDebounce
            onClick={() => setSeeAllEnergy(prev => !prev)}
          >
            <FormattedMessage
              {...(seeAllEnergy ? glossary.showLess : glossary.showMore)}
            />
          </Button>
        </SelectedCurrencyWrapper>
      ),
      visibleOnSports: [Sport.BASEBALL],
      expanded: expandedCurrencies?.includes(InGameCurrencyAgreggated.ENERGY),
    },
    {
      id: InGameCurrencyAgreggated.BLUEPRINTS,
      icon: <Cards color="var(--c-rivals-purple-800)" />,
      amount: blueprintCardsCount,
      details: () => (
        <SelectedCurrencyWrapper>
          <Vertical gap={0.5} center>
            <Cards size={2} color="var(--c-rivals-purple-800)" />
            <BrandS color="var(--c-rivals-purple-800)">
              <FormattedMessage
                id="useUserCurrencies.cardsToReveal"
                defaultMessage="Cards to reveal"
              />
            </BrandS>
            <BrandL>
              <FormattedNumber value={blueprintCardsCount || 0} />
            </BrandL>
          </Vertical>
          <Button
            size="medium"
            fullWidth
            color="quaternary"
            to={generateSportPath(ANY_SPORT_MY_CARDS_REVEALABLE_CARDS_TYPE, {
              sport,
              params: {
                type: Rarity.limited,
              },
            })}
          >
            <FormattedMessage
              id="useUserCurrencies.goToReveal"
              defaultMessage="Go to Reveal"
            />
          </Button>
        </SelectedCurrencyWrapper>
      ),
      onClick: () =>
        navigate(
          generateSportPath(ANY_SPORT_MY_CARDS_REVEALABLE_CARDS_TYPE, {
            sport,
            params: {
              type: Rarity.limited,
            },
          })
        ),
      visibleOnSports: [Sport.FOOTBALL, Sport.NBA, Sport.BASEBALL],
      expanded: expandedCurrencies?.includes(
        InGameCurrencyAgreggated.BLUEPRINTS
      ),
    },
    {
      id: InGameCurrencyAgreggated.GEMS,
      icon: <GemIcon size={2} />,
      amount: gemsCount,
      details: () => (
        <SelectedCurrencyWrapper>
          <Vertical gap={0.5} center>
            <GemIcon size={2} />
            <BrandS color="var(--c-green-400)">
              <FormattedMessage
                id="useUserCurrencies.gems"
                defaultMessage="Gems"
              />
            </BrandS>
            <BrandL>
              <FormattedNumber value={gemsCount || 0} />
            </BrandL>
          </Vertical>
          <Button
            size="medium"
            fullWidth
            color="quaternary"
            to={`${FOOTBALL_MARKET_COMMON_PACKS}?buy_gems=true`}
          >
            <FormattedMessage
              id="useUserCurrencies.getGems"
              defaultMessage="Get Gems"
            />
          </Button>
        </SelectedCurrencyWrapper>
      ),
      onClick: () => navigate(`${FOOTBALL_MARKET_COMMON_PACKS}?buy_gems=true`),
      visibleOnSports: [Sport.FOOTBALL],
      expanded: expandedCurrencies?.includes(InGameCurrencyAgreggated.GEMS),
    },
  ].filter(Boolean);
};
